/* eslint-disable react/jsx-props-no-spreading */
import { Box, CircularProgress } from '@mui/material';
import { BrowseDocumentsButton } from 'components/button/styles';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DOCUMENTS__PROCESSING, DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED } from 'translations/constants';
import { ALLOWED_IMAGE_MIMES, isImageFile, isRawImageFormat, resizeImage } from 'utils/fileValidation';
import { DragAndDropParagraph, DropArea, Error, FileName, ProcessingText } from './style';
import { UPDropzoneProps } from './types';

const UPDropzone = (props: UPDropzoneProps): JSX.Element => {
  const { onChange, instructionText, buttonText, noDrag, disabled, onValidationError } = props;
  const [name, setName] = useState<string>();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();

  const getDocumentName = (n: string): string => {
    if (n.length > 39) {
      return `${n.substring(0, 39)}...`;
    }
    return n;
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noKeyboard: true,
    accept: {
      ...ALLOWED_IMAGE_MIMES.reduce((acc, mime) => ({ ...acc, [mime]: [] }), {}),
      'application/pdf': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
    },
    noDrag,
    disabled,
    multiple: false,
    onDrop: async acceptedFiles => {
      setError(undefined);

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        if (isRawImageFormat(file.name)) {
          setName(getDocumentName(file.name)); // Show the filename even though it's invalid
          setError(t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED));
          onValidationError?.(t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED));
          return; // Don't call onChange - file is not accepted
        }

        // Resize image if needed
        let fileToUpload: File = file;
        if (isImageFile(file)) {
          setIsLoading(true);
          try {
            fileToUpload = await resizeImage(file);
          } catch (resizeError) {
            // Continue with original file if resize fails
          } finally {
            setIsLoading(false);
          }
        }

        setName(getDocumentName(file.name));
        onChange(fileToUpload);
      }
    },
    onDropRejected: fileRejections => {
      if (fileRejections.length > 0) {
        const { file } = fileRejections[0];

        // Show the filename even though it's rejected
        setName(getDocumentName(file.name));

        // Check if it's a RAW format
        if (isRawImageFormat(file.name)) {
          setError(t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED));
          onValidationError?.(t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED));
        } else {
          // Other rejection reason (wrong file type)
          setError('File type not supported. Please upload an image, PDF, or Word document.');
        }
      }
    },
    preventDropOnDocument: false,
    maxFiles: 1,
  });

  return (
    <DropArea {...getRootProps()} style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.7 : 1 }}>
      <input {...getInputProps()} disabled={disabled || isLoading} />
      {isLoading && (
        <Box display="flex" alignItems="center" gap="8px" marginBottom="8px">
          <CircularProgress size={16} />
          <ProcessingText>{t(DOCUMENTS__PROCESSING)}</ProcessingText>
        </Box>
      )}
      {!isLoading && !name && <DragAndDropParagraph>{instructionText}</DragAndDropParagraph>}
      {!isLoading && name && <FileName>{name}</FileName>}
      <BrowseDocumentsButton
        onClick={e => {
          e.stopPropagation();
          open();
        }}
        variant="outlined"
        disabled={isLoading}
      >
        {buttonText}
      </BrowseDocumentsButton>
      {error && <Error>{error}</Error>}
    </DropArea>
  );
};

UPDropzone.defaultProps = {
  noDrag: false,
  disabled: false,
};
export default UPDropzone;
